import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import ListCreateButton from 'components/buttons/ListCreateButton';
import { BadgeOutlined as BadgeOutlinedIcon } from 'components/icons';
import { useMemo } from 'react';

import { RecordLink } from 'components/record-chip/RecordLink';
import { Link as RouterLink } from 'react-router-dom';

import type { DataTableColumnDef } from 'components/tables/data-table';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';

import StatusChip from 'components/@extended/StatusChip';
import VacationMarker from 'components/employee/VacationMarker';
import Unmasked from 'components/Unmasked';
import Loader from 'components/Loader';
import { getFilterOptionsFromClassifier, getSortedFilterOptions } from 'components/tables/list-table/filterUtil';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import { DraftNameCell } from 'components/drafts/DraftNameCell';
import { useWithDrafts } from 'components/drafts/useWithDrafts';
import { useLazyGetAllEmployeesListWithFiltersQuery, useLazyGetEmployeesWithFiltersQuery } from 'store/api/employees';
import { useLazyGetJobPositionsWithFiltersQuery } from 'store/api/jobPositions';
import { useLazyGetJobRolesWithFiltersQuery } from 'store/api/jobRoles';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import { useLazyGetPracticeAreasWithFiltersQuery } from 'store/api/practice-areas';
import { useGetSystemRolesListQuery } from 'store/api/systemRoles';
import { useLazyGetMinTeamsListWithFiltersQuery } from 'store/api/teams';
import { Employee } from 'types/employees';
import { FeatureFlag } from 'types/features';
import { PracticeAreaType } from 'types/practice-areas';
import { TeamType } from 'types/teams';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { formatEmployeeName, useLastNameFirst } from 'utils/nameOrder';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useHasPermissions } from 'utils/permissionUtil';
import { getPracticeAreas } from './util';

const getEmployeeHref = (employee: Employee) => `/employee/${employee.id}`;

const EmployeesTable = () => {
    const navigateWithReturn = useNavigateWithReturnState();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['LANGUAGE'],
    });
    const [fetchEmployees, { data, isFetching }] = useLazyGetEmployeesWithFiltersQuery();
    const fetchData = useWithDrafts(fetchEmployees);
    const isLocalizedFieldsEnabled = useIsFeatureEnabled(FeatureFlag.LOCALIZED_FIELDS);
    const lastNameFirst = useLastNameFirst();
    const { data: systemRoles } = useGetSystemRolesListQuery();
    const canReadPersonalData = useHasPermissions(['READ.EMPLOYEE_PERSONAL.ALL']);

    // Build default filters for employees (active employees by default)
    const initialSearchParams = 'qf=isActive=in=(true)';

    const columns = useMemo<DataTableColumnDef<Employee>[]>(
        () => [
            {
                header: t('name'),
                id: 'name',
                // A column is sortable only when it has an accessor; the value itself is unused
                // (the cell overrides rendering). sortAccessor flips the server sort key between
                // first and last name per the LAST_NAME_FIRST flag.
                accessorKey: 'firstName',
                meta: {
                    sortAccessor: lastNameFirst ? 'lastName' : 'firstName',
                    widthWeight: 1.19,
                    minPx: 200,
                },
                cell: ({ cell }) => {
                    if (!cell?.row?.original) {
                        return null;
                    } else {
                        const employee = cell.row.original;
                        return (
                            <DraftNameCell draft={employee.isDraft ? { type: 'employee', id: employee.id } : null}>
                                {getEmployeeName(employee, lastNameFirst)}
                            </DraftNameCell>
                        );
                    }
                },
            },
            {
                header: t('contacts.contact-details'),
                accessorKey: 'workPhone',
                enableSorting: false,
                meta: {
                    widthWeight: 1.33,
                    minPx: 220,
                },
                cell: ({ cell }) => {
                    const { workPhone, mobilePhone } = cell.row.original;
                    return (
                        <div className="flex w-fit flex-col gap-1">
                            {workPhone?.length ? (
                                <span className="text-body1">
                                    {t('work-phone')}:&nbsp;{workPhone}
                                </span>
                            ) : null}
                            {mobilePhone?.length ? (
                                <span className="text-body1">
                                    {t('mobile')}:&nbsp;{mobilePhone}
                                </span>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                header: t('status.status'),
                accessorKey: 'isActive',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ getValue }) => {
                    switch (getValue()) {
                        case false:
                            return <StatusChip label={t('not-active')} type="error" />;
                        case true:
                            return <StatusChip label={t('active')} type="success" />;
                        default:
                            return <StatusChip label={t('unknown')} type="inactive" />;
                    }
                },
            },
            {
                header: t('practice-areas.practice-areas'),
                accessorKey: 'practiceAreas',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 150,
                },
                cell: ({ cell }) => {
                    const { practiceAreas } = cell?.row?.original ?? {};
                    return getPracticeAreas(practiceAreas);
                },
            },
            {
                header: t('teams.core-team'),
                accessorKey: 'coreTeam',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 130,
                },
                cell: ({ cell }) => {
                    const { latestEmployment } = cell?.row?.original ?? {};
                    if (latestEmployment?.coreTeamName) {
                        return <Unmasked>{latestEmployment.coreTeamName}</Unmasked>;
                    } else {
                        return null;
                    }
                },
            },
        ],
        [lastNameFirst]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [
                {
                    selector: 'isActive',
                    chipLabel: 'filters.status',
                    filters: [
                        {
                            label: t('active'),
                            argument: 'true',
                        },
                        {
                            label: t('not-active'),
                            argument: 'false',
                        },
                    ],
                },
            ],
            filters: [
                {
                    type: 'text',
                    label: t('name'),
                    selector: 'fullName',
                    group: 'employees.employee',
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('office'),
                    selector: 'currentEmployment.office.id',
                    group: 'employees.employee',
                    paginated: {
                        fetch: useLazyGetMinOfficesWithFiltersQuery,
                        searchField: 'name',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                    getOptionEndAdornment: officeInactiveSuffix,
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('teams.core-team'),
                    selector: 'currentEmployment.coreTeam.id',
                    group: 'employees.employee',
                    paginated: {
                        fetch: useLazyGetMinTeamsListWithFiltersQuery,
                        searchField: 'name',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('employees.support-teams'),
                    selector: 'employeeTeams.team.id',
                    group: 'employees.employee',
                    paginated: {
                        fetch: useLazyGetMinTeamsListWithFiltersQuery,
                        searchField: 'name',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('contacts.filter.sector-group'),
                    selector: 'id',
                    comperator: '=isinsectorgroup=',
                    tooltip: 'employees.tooltips.filter-by-sector-group',
                    group: 'employees.employee',
                    paginated: {
                        fetch: useLazyGetMinTeamsListWithFiltersQuery,
                        searchField: 'name',
                        filters: `teamTypeCode==${TeamType.SECTOR_GROUP};isParent==true`,
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('practice-areas.practice-areas'),
                    selector: 'practiceAreas.practiceArea.id',
                    group: 'employees.employee',
                    paginated: {
                        fetch: useLazyGetPracticeAreasWithFiltersQuery,
                        searchField: 'name',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                        filters: `practiceAreaTypeCode==${PracticeAreaType.PRACTICE_AREA}`,
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('supervisor'),
                    selector: 'manager.id',
                    group: 'employees.employee',
                    paginated: {
                        fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'offset-date-time-range',
                    label: t('employees.employment-start-date'),
                    selector: 'employmentStartDate',
                    comperator: '=daterange=',
                    shouldDisplayQuickPick: false,
                    group: 'employees.employee',
                },
                {
                    type: 'multiselect',
                    label: t('employees.languages-spoken'),
                    selector: 'languagesSpoken.languageCode',
                    group: 'employees.employee',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['LANGUAGE']) : [],
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('job-roles.job-role'),
                    selector: 'currentEmployment.jobRole.id',
                    group: 'employees.employee',
                    paginated: {
                        fetch: useLazyGetJobRolesWithFiltersQuery,
                        searchField: 'name',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('job-positions.job-position'),
                    selector: 'currentEmployment.jobPosition.id',
                    group: 'employees.employee',
                    paginated: {
                        fetch: useLazyGetJobPositionsWithFiltersQuery,
                        searchField: 'name',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('employees.filter.delegates-tasks-to'),
                    selector: 'taskReceivers.receiver.id',
                    group: 'employees.employee',
                    paginated: {
                        fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('employees.filter.receives-tasks-from'),
                    selector: 'receivingTasksForEmployees.sharer.id',
                    group: 'employees.employee',
                    paginated: {
                        fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'multiselect',
                    label: t('employees.system-roles'),
                    selector: 'employeeSystemRoles.systemRole.id',
                    group: 'employees.employee',
                    options: systemRoles ? getSortedFilterOptions('name', (r) => String(r.id), systemRoles) : [],
                },
                // The backend refuses this selector without READ.EMPLOYEE_PERSONAL.ALL.
                ...(canReadPersonalData
                    ? [
                          {
                              type: 'checkboxgroup' as const,
                              selector: 'isPoliticallyExposedPerson',
                              group: 'employees.other.title',
                              filters: [
                                  {
                                      label: t('employees.politically-exposed-person'),
                                      argument: 'true',
                                  },
                              ],
                          },
                      ]
                    : []),
                {
                    type: 'checkboxgroup',
                    selector: 'isActive',
                    group: 'employees.other.title',
                    filters: [
                        {
                            label: t('active'),
                            argument: 'true',
                        },
                        {
                            label: t('not-active'),
                            argument: 'false',
                        },
                    ],
                },
            ],
            searchBarSelectors: [
                { selector: 'workEmailAddress' },
                { selector: 'fullName' },
                { selector: 'mobilePhone', comparator: '=phonelk=' },
                { selector: 'workPhone', comparator: '=phonelk=' },
                { selector: 'coreTeam.name' },
                { selector: 'employeeTeams.team.name' },
                { selector: 'currentEmployment.office.name' },
                { selector: 'employmentHistory.jobRole.name' },
                { selector: 'employmentHistory.jobRole.internalName' },
                ...(isLocalizedFieldsEnabled ? [{ selector: 'localFullName' }] : []),
            ],
        }),
        [isLocalizedFieldsEnabled, classifiers, systemRoles, canReadPersonalData]
    );

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<Employee>
            getRowHref={getEmployeeHref}
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="employees"
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="employees.colleagues"
            singularTextId="employees.colleague"
            noEntriesId="employees.no-employees-found"
            noEntriesBodyId="employees.empty.body"
            noEntriesIcon={BadgeOutlinedIcon}
            noEntriesAction={
                <ListCreateButton
                    permissions={['CREATE.EMPLOYEES']}
                    variant="contained"
                    color="secondary"
                    onClick={() => navigateWithReturn('/employee/new')}
                    labelId="employees.add-new-employee"
                    newLabelId="new-entity.employee"
                />
            }
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={50}
            initialSearchParams={initialSearchParams}
            canRememberFilters={true}
        />
    );
};

// `entity-link` hands the cell's link colour and wrapping to the table's own rule.
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';

const getEmployeeName = (employee: Employee, lastNameFirst: boolean) => {
    return (
        <div className="flex flex-col">
            <RouterLink className={cn(LINK_CLASS, 'font-semibold')} to={`/employee/${employee.id}`}>
                {employee ? (
                    <>
                        <VacationMarker onVacation={employee.onVacation} />
                        {formatEmployeeName({ ...employee, onVacation: false }, lastNameFirst) ||
                            (employee.isDraft ? t('new-entity.employee') : '')}
                    </>
                ) : (
                    '-'
                )}
            </RouterLink>
            <div>
                {employee.latestEmployment ? (
                    <>
                        {employee.latestEmployment.jobPositionName
                            ? employee.latestEmployment.jobPositionName + ','
                            : null}
                        {employee.latestEmployment.jobPositionName ? `\u00A0` : ''}
                    </>
                ) : null}
                {employee?.latestEmployment?.officeId ? (
                    <RecordLink
                        className={LINK_CLASS}
                        to={`/office/${employee.latestEmployment.officeId}`}
                        label={employee.latestEmployment?.officeName ?? '-'}
                    />
                ) : null}
            </div>
        </div>
    );
};

export default EmployeesTable;
